import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { debounceTime, distinctUntilChanged, skip, Subscription } from 'rxjs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import {
  LOGS_FILTER_OPTIONS,
  LOGS_FILTER_TABS,
  LOGS_DEFAULT_COLUMNS_SORT,
} from '../logs.constants';
import { LogsApiResponse } from '../logs.models';
import { LogsService } from '../logs.service';
import { FilterDropdownComponent } from '../../../../../ui-components/lib/filter-dropdown/filter-dropdown.component';
import { PaginatorComponent } from '../../../../../ui-components/lib/paginator/paginator.component';
import { AvatarComponent } from '../../../../../ui-components/lib/avatar/avatar.component';
import { BadgeComponent } from '../../../../../ui-components/lib/badge/badge.component';
import { SearchFieldComponent } from '../../../../../ui-components/lib/search-field/search-field.component';
import { TableComponent } from '../../../../../ui-components/lib/table/table.component';
import { FilterDropdownService } from '../../../../../ui-components/lib/filter-dropdown/filter-dropdown.service';
import {
  TableFilterTab,
  TableColumns,
} from '../../../../../ui-components/lib/table/table.model';
import { FilterOption } from '../../../../../ui-components/lib/filter-dropdown/filter-dropdown.model';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../../../../common/constants/pagination.constants';
import { SearchEnum } from '../../../../common/enums/search.enum';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';
import {
  injectFloatingLayout,
  injectFloatingPageClasses,
  provideSystemTabsLayout,
} from '../../../../common/helpers/system-tabs-layout/system-tabs-layout';

@Component({
  selector: 'lib-logs-list',
  imports: [
    BadgeComponent,
    TableComponent,
    TranslocoPipe,
    FilterDropdownComponent,
    DatePipe,
    NgTemplateOutlet,
    AvatarComponent,
    PaginatorComponent,
    SearchFieldComponent,
  ],
  providers: [provideSystemTabsLayout()],
  templateUrl: './logs-list.component.html',
  styleUrl: './logs-list.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class LogsListComponent implements OnInit, OnDestroy {
  protected readonly floating = injectFloatingLayout();
  protected readonly floatingPageClasses = injectFloatingPageClasses();
  router = inject(Router);
  route = inject(ActivatedRoute);
  transloco = inject(TranslocoService);
  filterService = inject(FilterDropdownService);
  logsService = inject(LogsService);
  featureFlagsService = inject(FeatureFlagsService);
  /** Tables_new_design moves the search from the panel header into the table toolbar. */
  protected readonly searchInToolbar = computed(
    () =>
      this.floating() &&
      this.featureFlagsService.getFeatureFlagValue(
        FEATURE_FLAGS.TABLE.ui_enhancement
      )
  );

  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS;
  searchEnum = SearchEnum;
  readonly searchInputWidth = SearchEnum.searchInputWidth;
  searchTerm = signal<string>('');
  isMoreThanThreeCharacters = signal<boolean>(false);
  private readonly searchTerm$ = toObservable(this.searchTerm);

  totalItems = signal<string>('100');
  isLoading = signal<boolean>(false);
  dataSource = signal<LogsApiResponse[]>([]);
  currentPage = signal<number>(1);
  currentSize = signal<number>(25);
  totalPages = signal<number>(1);
  totalCount = signal<number>(0);
  isInitialLoad = true;
  tableFilterTabs = signal<TableFilterTab[]>(
    LOGS_FILTER_TABS.map(tab => ({
      ...tab,
      label: this.transloco.translate(tab.label),
    }))
  );
  activeTableFilterTab = signal<TableFilterTab>(this.tableFilterTabs()[0]);
  initialFilterValues = signal<Record<string, string | string[]>>({});

  defaultColumns = signal<TableColumns[]>(
    LOGS_DEFAULT_COLUMNS_SORT.map(column => ({
      ...column,
      header: this.transloco.translate(column.header),
    }))
  );

  filterOptions: FilterOption[] = LOGS_FILTER_OPTIONS.map(option => ({
    ...option,
    label: this.transloco.translate(option.label),
    placeholder: option.placeholder
      ? this.transloco.translate(option.placeholder)
      : '',
  }));

  filters = signal<Record<string, string | string[]>>({});

  defaultSort = signal<{ key: string; value: 'asc' | 'desc' }>({
    key: '',
    value: 'asc',
  });
  activeSort = signal<{ key: string; value: 'asc' | 'desc' }>(
    this.defaultSort()
  );

  columns = signal<TableColumns[]>(this.defaultColumns());
  subscriptions = new Subscription();

  ngOnInit(): void {
    this.handleParamsChange();
    this.getLogsList();
    this.searchTermWrapper();
  }

  searchTermWrapper() {
    this.subscriptions.add(
      this.searchTerm$
        .pipe(skip(1), debounceTime(300), distinctUntilChanged())
        .subscribe(term => {
          this.handleSearchTerm(term);
        })
    );
  }

  onSearchTermChange(term: string) {
    this.searchTerm.set(term);
  }

  handleSearchTerm(term: string): void {
    this.currentPage.set(1);

    if (this.isSearchable(term)) {
      this.isMoreThanThreeCharacters.set(true);
      this.getLogsList();
      return;
    }

    if (this.isMoreThanThreeCharacters()) {
      this.isMoreThanThreeCharacters.set(false);
      this.getLogsList();
    }
  }

  private isSearchable(term: string): boolean {
    const normalized = term.trim();
    if (/^id-?\d+$/i.test(normalized) || /^\d+$/.test(normalized)) {
      return normalized.length > 0;
    }

    return normalized.length >= SearchEnum.minSearchLength;
  }

  private getLogsList(): void {
    this.isLoading.set(true);
    const filters = this.isSearchable(this.searchTerm())
      ? { ...this.filters(), keyword: this.searchTerm().trim() }
      : this.filters();
    this.subscriptions.add(
      this.logsService
        .getLogsList(
          this.currentPage(),
          this.currentSize(),
          filters,
          this.activeSort()
        )
        .subscribe({
          next: (res: any) => {
            this.dataSource.set(res.data);
            this.totalPages.set(res.meta.last_page);
            this.totalCount.set(res.meta.total);
            this.isLoading.set(false);
          },
          error: () => {
            this.isLoading.set(false);
          },
        })
    );
  }

  onApplyFilters(filters: Record<string, string | string[]>) {
    const currentQueryParams = this.route.snapshot.queryParams;
    const queryParams = this.filterService.mergeFiltersWithQueryParams(
      filters,
      this.filterOptions,
      currentQueryParams
    );
    this.filters.set(queryParams);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true,
    });
  }

  handlePagination($event: number) {
    this.currentPage.set($event);
    this.getLogsList();
  }

  handleSortColumn(column: TableColumns) {
    if (column.isSorted) {
      this.activeSort.set({
        key: column.sortKey || '',
        value: column.isSorted,
      });
    } else {
      this.activeSort.set(this.defaultSort());
    }
    this.currentPage.set(1);
    this.getLogsList();
  }

  changeSize($event: number) {
    this.currentSize.set($event);
    this.currentPage.set(1);
    this.getLogsList();
  }

  handleParamsChange() {
    this.subscriptions.add(
      this.route.queryParams.subscribe(params => {
        // Update filters from URL
        const filterValues = this.filterService.queryParamsToFilters(
          params as Record<string, string | string[]>,
          this.filterOptions
        );
        this.filters.set(filterValues);
        this.initialFilterValues.set(filterValues);
        this.getLogsList();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.filterService.resetState();
  }
}
